'use client';

import { useEffect } from 'react';
import { getChatToken, getEnvironmentConfig } from '@repo/api';
import { useStore } from '@/providers/StoreProvider';
import { ensureSession } from '@/lib/api';
import { getNotificationWebSocket } from '@/lib/chat/NotificationWebSocket';

/**
 * Provider that keeps unread chat notifications in sync via WebSocket.
 * Fetches one-time token from API and passes it as `one_time_token` query param.
 */
export function NotificationPolling() {
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const setUnreadMessages = useStore(state => state.chat?.setUnreadMessages);
  const setTourFeedbackPrompts = useStore(state => state.chat?.setTourFeedbackPrompts);

  useEffect(() => {
    const wsService = getNotificationWebSocket();
    let isCancelled = false;

    if (!isLoggedIn || !setUnreadMessages) {
      wsService.disconnect();
      return;
    }

    const connectNotificationSocket = async () => {
      try {
        if (!(await ensureSession()) || isCancelled) return;
        const tokenResponse = await getChatToken();
        const token = tokenResponse.token;

        if (isCancelled || !token) {
          return;
        }

        const config = getEnvironmentConfig();
        const baseUrl = config.endpoints.websocketChat;
        const socketUrl = new URL('/chat/notifications', baseUrl).toString();

        wsService.connect({
          url: socketUrl,
          token,
          handlers: {
            onUnreadCount: count => {
              if (!isCancelled) {
                setUnreadMessages(count);
              }
            },
            onTourFeedbackPrompts: prompts => {
              if (!isCancelled) {
                setTourFeedbackPrompts?.(prompts);
              }
            },
          },
          onError: error => {
            console.error('[notifications-ws] error', error);
          },
        });
      } catch (error) {
        console.error('Error connecting notification socket:', error);
        wsService.disconnect();
      }
    };

    void connectNotificationSocket();

    return () => {
      isCancelled = true;
      wsService.disconnect();
    };
  }, [isLoggedIn, setUnreadMessages, setTourFeedbackPrompts]);

  return null;
}
